import { latLonToEN } from '@openlayers/ol-utils';
import VectorLayer from 'ol/layer/Vector';
import VectorSource from 'ol/source/Vector';
import Feature from 'ol/Feature';
import { LineString, Point as OlPoint } from 'ol/geom';
import * as olStyle from 'ol/style';
import { MapWrapper } from '@openlayers/MapWrapper';


export class SelectionMapMarkers {
    private readonly layer: VectorLayer<any>;
    private readonly destLines = [];
    private readonly circles = [];

    private readonly styles = {
        circle: new olStyle.Style({
            image: new olStyle.Circle({
                radius: 15,
                fill: new olStyle.Fill({
                    color: '#4797CC',
                }),
            }),
        }),
        circle2: new olStyle.Style({
            image: new olStyle.Circle({
                radius: 23,
                fill: new olStyle.Fill({
                    color: [71, 151, 204, 0.3],
                }),
            }),
        }),
        line: new olStyle.Style({
            stroke: new olStyle.Stroke({
                color: '#4797CC',
                width: 2,
            }),
        }),
        destination: new olStyle.Style({
            image: new olStyle.Circle({
                radius: 5,
                fill: new olStyle.Fill({
                    color: '#4797CC',
                }),
            }),
        }),
    };

    constructor(private mapWrapper: MapWrapper) {
        this.layer = new VectorLayer({
            source: new VectorSource(),
        });

        mapWrapper.addLayer(this.layer, 1, 'selection-marker');
    }

    destroy() {
        this.mapWrapper.removeLayer(this.layer);
    }

    showDestinationLines(show = true) {
        // destination lines are always on canvas, but this will enable/disable rendering
        if (show) {
            this.layer.setStyle([this.styles.circle2, this.styles.circle, this.styles.line]);
        }
        else {
            this.removeDestinationCircle();
            this.layer.setStyle([this.styles.circle2, this.styles.circle]);
        }
    }

    setDestinationLinesTarget(lat: number, lon: number) {
        lat = Math.max(-90, Math.min(lat, 90));
        lon = Math.max(-180, Math.min(lon, 180));

        this.destLines.forEach(line => {
            const coords = line.getGeometry().getCoordinates();
            if (coords[0][0] < 0.01 && coords[0][1] < 0.01) {
                coords[0] = latLonToEN(lat, lon); // No line if start is at zero latlon
            }
            coords[1] = latLonToEN(lat, lon);
            line.getGeometry().setCoordinates(coords);
        });

        this.circles.forEach(circle => {
            let coords = circle.getGeometry().getCoordinates();
            if (coords[0] < 0.01 && coords[1] < 0.01) {
                coords = latLonToEN(lat, lon); // Move circle to destination if start is at zero latlon
                circle.getGeometry().setCoordinates(coords);
            }
        });

        // destination circle
        this.removeDestinationCircle();
        const destination = latLonToEN(lat, lon);
        const circle = new Feature({
            geometry: new OlPoint(destination),
        });
        circle.setId('destination');
        circle.setStyle(this.styles.destination);
        this.layer.getSource().addFeature(circle);
    }

    drawMarkersDestinationLines(items: {
        geographicPoint: { lat: number, lon: number }
    }[]) {
        const source = this.layer.getSource();
        source.clear();

        items.forEach(item => {
            const start = latLonToEN(item.geographicPoint?.lat || 0 , item.geographicPoint?.lon || 0);

            try {
                const circle = new Feature({
                    geometry: new OlPoint(start),
                });

                const line = new Feature({
                    geometry: new LineString([start, start]),
                });

                this.circles.push(circle);
                this.destLines.push(line);

                source.addFeature(line);
                source.addFeature(circle);
            } catch {}
        });
    }

    private removeDestinationCircle = () => {
        const source = this.layer.getSource();
        const existing = source.getFeatureById('destination');
        if (existing) {
            source.removeFeature(existing);
        }
    }
}
